import React, { useMemo, useState } from 'react';
import { compose } from 'redux';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Moment from 'moment';
import Interweave from 'interweave';
import { Box, Tab, Tabs } from '@mui/material';
import { useTheme, withStyles } from '@mui/styles';
import { useFeatureIsOn } from '@growthbook/growthbook-react';

// dev-express table system
import { useQueryParams } from '../../components/providers/QueryParamsProvider';
import { useAPIErrorHandler } from '../../components/hooks/useAPIErrorHandler';
import { useSavedSettings } from '../../components/tables/dev-express/hooks/useSavedSettings';
import { useBackfill } from '../../components/tables/dev-express/hooks/useBackfill';
import { useTableState } from '../../components/tables/dev-express/hooks/useTableState';
import { useQueryHandlers } from '../../components/tables/dev-express/hooks/useQueryHandlers';
import { useUiSettingsAutosave } from '../../components/tables/dev-express/hooks/useUiSettingsAutosave';
import { PrimaryToolbar } from '../../components/tables/dev-express/table-components/toolbar/PrimaryToolbar';
import { SecondaryToolbar } from '../../components/tables/dev-express/table-components/toolbar/SecondaryToolbar';
import { TableSearch } from '../../components/tables/dev-express/table-components/toolbar/TableSearch';
import { ColumnChooser } from '../../components/tables/dev-express/table-components/toolbar/ColumnChooser';
import { CsvDownloadButton } from '../../components/tables/dev-express/table-components/toolbar/CsvDownloadButton';
import { RefreshButton } from '../../components/tables/dev-express/table-components/toolbar/RefreshButton';
import { TableStateProvider } from '../../components/tables/dev-express/TableStateContext';
import { BaseTable } from '../../components/tables/dev-express/BaseTable';
import { RemotePagingPlugin } from '../../components/tables/dev-express/plugins/RemotePagingPlugin';
import { ColumnResizingPlugin } from '../../components/tables/dev-express/plugins/ColumnResizingPlugin';
import type { ColumnConfig } from '../../components/tables/dev-express/types/table.types';

import { CowbellFactorsMapping } from '../../utility/CowbellFactorsMapping';
import SimpleTooltip from '../../components/SimpleTooltip';
import ToolTipIcon from '../../_assets/svg/Tooltip.svg';
import { getInsights } from './InsightsService';
import { toUniversalDate, toUniversalUtcDate } from '../../utils/date.utils';
import { FEATURE_FLAG_KEYS } from '../../config/growthbook';
import { ThemedSpotlightIcon } from '../../components/spotlight/SpotlightBanner';
import { getTooltipTitle } from './getTooltipTitle';
import { ActiveTab, TableId } from './spotlight-details/spotlightDetails.types';

const SAVE_SETTINGS_ID = 'reasonsRecommendations';

const BACKFILL = {
  size: 25,
  order: 'desc',
  orderBy: 'created',
  before: () => Moment().endOf('day').unix(),
  after: () => Moment().subtract(3, 'years').unix(),
};

const styles = (theme: any) => ({
  tagIcon: {
    width: '1.5rem',
    height: '1.5rem',
    margin: '0 1.5rem',
    background: `url('images/Actionable.svg')`,

    '&:hover': {
      background: `url('images/ActionableBlueLit.svg')`,
    },
  },
  tagIcon1: {
    width: '1.5rem',
    height: '1.5rem',
    margin: '0 1.5rem',
  },
  cowbellIcon: {
    width: '7rem',
    height: '5rem',
  },
  msTagIcon: {
    width: '3.5rem',
    height: '3.5rem',
    margin: '0 0.5rem',
  },
  cfIcon: {
    width: '3.5rem',
    height: '3.5rem',
    margin: 0,
  },
  ssTagIcon: {
    width: '1.5rem',
    height: '1.5rem',
    margin: '0 1.5rem',
  },
  safeGuardTagIcon: {
    width: '2.5rem',
    height: '2.5rem',
    margin: '0 1rem',
  },
  dataSourceContainer: {
    display: 'flex',
    flexDirection: 'row' as const,
  },
  legendIcon: {
    width: '0.833rem',
    height: '0.833rem',
    margin: '3px 10px 0 0',
  },
  legendText: {
    fontSize: '1rem',
    fontWeight: 600,
    lineHeight: 1.33,
    margin: '0px !important',
  },
  headingMargins: {
    marginTop: '0',
    marginBottom: '0',
  },
  tabsContainer: {
    width: 'fit-content',
    padding: '0',
    margin: '0',
    marginRight: '2rem',
    boxShadow: 'none',
    '& .MuiTabs-root': {
      minHeight: 'auto',
      padding: '0',
      margin: '0',
    },
    '& .MuiTabs-flexContainer': {
      gap: '0.25rem',
    },
  },
  tabContainer: {
    minHeight: '1.75rem',
    fontSize: '0.875rem',
    fontWeight: 500,
    padding: '0.25rem 0.5rem',
    margin: '0',
    color: theme.palette.button.text,
    borderRadius: '0.25rem',
    '&.Mui-selected': {
      backgroundColor: theme.palette.primary.dark,
      color: theme.palette.button.text2,
    },
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
      color: theme.palette.button.text2,
    },
  },
  mainContainer: {
    padding: '0',
  },
});

const CF_MAPPINGS = {
  'Company Aggregate': {
    tooltip: CowbellFactorsMapping.companyAggregate.tooltip,
    color: CowbellFactorsMapping.companyAggregate.color,
  },
  'Industry Aggregate': {
    tooltip: CowbellFactorsMapping.industryAggregate.tooltip,
    color: CowbellFactorsMapping.industryAggregate.color,
  },
  'Network Security': {
    tooltip: CowbellFactorsMapping.networkSecurity.tooltip,
    color: CowbellFactorsMapping.networkSecurity.color,
  },
  'Cloud Security': {
    tooltip: CowbellFactorsMapping.cloudSecurity.tooltip,
    color: CowbellFactorsMapping.cloudSecurity.color,
  },
  'Endpoint Security': {
    tooltip: CowbellFactorsMapping.endpointSecurity.tooltip,
    color: CowbellFactorsMapping.endpointSecurity.color,
  },
  'Dark Intelligence': {
    tooltip: CowbellFactorsMapping.darkIntelligence.tooltip,
    color: CowbellFactorsMapping.darkIntelligence.color,
  },
  'Funds Transfer': {
    tooltip: CowbellFactorsMapping.fundsTransfer.tooltip,
    color: CowbellFactorsMapping.fundsTransfer.color,
  },
  'Cyber Extortion': {
    tooltip: CowbellFactorsMapping.cyberExtortion.tooltip,
    color: CowbellFactorsMapping.cyberExtortion.color,
  },
  Compliance: {
    tooltip: CowbellFactorsMapping.compliance.tooltip,
    color: CowbellFactorsMapping.compliance.color,
  },
};

const DATA_SOURCE_CSS = {
  'Cowbell Cyber': 'cowbellIcon',
  'Secureworks Taegis VDR': 'tagIcon1',
  'Microsoft Secure Score': 'msTagIcon',
  'GCP Security Command Center': 'msTagIcon',
  'AWS Security Hub': 'tagIcon1',
  AWS: 'msTagIcon',
  Secureworks: 'tagIcon1',
  'Security Studio': 'ssTagIcon',
  'Qualys Vulnerability Management': 'ssTagIcon',
  'Qualys Policy Compliance': 'ssTagIcon',
  'Qualys Container Security': 'ssTagIcon',
  'Wizer Cyber Training': 'ssTagIcon',
  'Safeguard Cyber': 'safeGuardTagIcon',
  'Google Workspace': 'msTagIcon',
  CLOUDFLARE: 'cfIcon',
  'SOPHOS ENDPOINT': 'msTagIcon',
};

const ActionableCell = ({ row, ctx }: Readonly<{ row: any; ctx: any }>) => {
  const { classes } = ctx;

  if (row?.isSpotlight) {
    return (
      <SimpleTooltip title={getTooltipTitle(row)} classes={{ tooltip: 'tt-wide' }}>
        <ThemedSpotlightIcon
          style={{ margin: '0.5rem 1.5rem 0 1.5rem', fontSize: '2.5rem' }}
        />
      </SimpleTooltip>
    );
  }

  if (row.actionable) {
    return (
      <SimpleTooltip title={getTooltipTitle(row)} classes={{ tooltip: 'tt-wide' }}>
        <div className={classes.tagIcon} />
      </SimpleTooltip>
    );
  }

  return (
    <img alt="" src="images/NonActionable.svg" className={classes.tagIcon1} />
  );
};

const DataSourceCell = ({ row, ctx }: Readonly<{ row: any; ctx: any }>) => {
  const { classes, connectors } = ctx;
  const icon = connectors.icons[row.dataSource]
    ? connectors.icons[row.dataSource]
    : '';
  const css = DATA_SOURCE_CSS[row.dataSource] ?? '';

  return (
    <section className={classes.dataSourceContainer}>
      <img alt="" src={icon} className={classes[css]} />
      <span style={{ display: 'flex', alignItems: 'center' }}>
        {row.dataSource === 'Cowbell Cyber' ? '' : row.dataSource}
      </span>
    </section>
  );
};

const MostImpactedCfCell = ({ row, ctx }: Readonly<{ row: any; ctx: any }>) => {
  const { classes } = ctx;

  if (row.mostImpactedCF === 'All') {
    return <span style={{ color: '#2180e2' }}>All</span>;
  }

  const mapping = CF_MAPPINGS[row.mostImpactedCF];
  const color = mapping ? mapping.color : '';
  const tooltip = mapping ? mapping.tooltip : '';

  return (
    <section className={classes.dataSourceContainer}>
      <div className={classes.legendIcon} style={{ backgroundColor: color }} />
      <p className={classes.legendText} style={{ color }}>
        {row.mostImpactedCF}
      </p>
      <SimpleTooltip title={`${tooltip}`} style={{ marginLeft: '0.2rem' }}>
        <ToolTipIcon />
      </SimpleTooltip>
    </section>
  );
};

const useColumnConfig = (isSpotlightV2PgSource: boolean): ColumnConfig =>
  useMemo(() => {
    const formatCreated = (row: any) =>
      isSpotlightV2PgSource
        ? toUniversalDate(row.created)
        : toUniversalUtcDate(row.created);

    return {
      actionable: {
        name: 'actionable',
        title: 'Actionable',
        sort: false,
        getCellValue: (row: any, ctx: any) => (
          <ActionableCell row={row} ctx={ctx} />
        ),
        csv: (row: any) => (row.actionable ? 'true' : 'false'),
      },
      reasonDescription: {
        name: 'reasonDescription',
        title: 'Description',
        width: 500,
        getCellValue: (row: any) =>
          row.reasonDescription ? (
            <Interweave content={row.reasonDescription} />
          ) : (
            ''
          ),
      },
      dataSource: {
        name: 'dataSource',
        title: 'Data Source',
        getCellValue: (row: any, ctx: any) => (
          <DataSourceCell row={row} ctx={ctx} />
        ),
      },
      mostImpactedCF: {
        name: 'mostImpactedCF',
        title: 'Most Impacted CFs',
        getCellValue: (row: any, ctx: any) => (
          <MostImpactedCfCell row={row} ctx={ctx} />
        ),
      },
      importance: {
        name: 'importance',
        title: 'Severity',
      },
      ipAddress: {
        name: 'ipAddress',
        title: 'Resource',
        sort: false,
        getCellValue: (row: any) => {
          if (!row.ipAddress) {
            return '-';
          }
          return row.ipAddress + (row.port ? `:${row.port}` : '');
        },
      },
      created: {
        name: 'created',
        title: 'Date',
        getCellValue: formatCreated,
        csv: formatCreated,
      },
    };
  }, [isSpotlightV2PgSource]);

const ReasonsRecommendationsTable = ({
  classes,
  accountId,
  recommendationTab,
  tableId,
}: Readonly<{
  classes: any;
  accountId: any;
  recommendationTab: any;
  tableId: any;
}>) => {
  const { query } = useQueryParams();
  const handleAPIError = useAPIErrorHandler();
  const { connectors } = useTheme();
  const isSpotlightV2PgSource = useFeatureIsOn(
    FEATURE_FLAG_KEYS.SPOTLIGHT_V2_PG_SOURCE
  );

  const columnConfig = useColumnConfig(isSpotlightV2PgSource);

  const { uiSettings } = useSavedSettings(SAVE_SETTINGS_ID, columnConfig);
  useBackfill(BACKFILL, uiSettings);

  const { queryHandlers } = useQueryHandlers(query);
  const { state, columns, handlers } = useTableState(columnConfig, {
    uiSettings,
  });

  useUiSettingsAutosave(
    SAVE_SETTINGS_ID,
    {
      columns,
      columnOrder: state.columnOrder,
      ...query,
    },
    ['before', 'after']
  );

  const queryClient = useQueryClient();

  const { data, isFetching } = useQuery({
    queryKey: [tableId, accountId, query],
    queryFn: () => getInsights({ accountId, ...query, recommendationTab }),
    select: (response: any) => ({
      rows: response.data.content.map((item: any) => ({
        ...item,
        id: item.findingId || item.reasonName || item.reasonDescription,
      })),
      totalCount: response.data.totalElements,
    }),
    onError: handleAPIError('Unable to retrieve reasons & recommendations.'),
  });

  const onRefetch = () => {
    queryClient.invalidateQueries([tableId]);
  };

  const context = useMemo(() => ({ classes, connectors }), [classes, connectors]);

  const mergedState = useMemo(() => ({ ...state, ...query }), [query, state]);
  const mergedHandlers = useMemo(
    () => ({ ...handlers, ...queryHandlers }),
    [handlers, queryHandlers]
  );

  return (
    <section className={classes.mainContainer}>
      <PrimaryToolbar>
        <Box display="flex" justifyContent="flex-end" width="100%">
          <Box width="25rem">
            <TableSearch placeholder="Search" />
          </Box>
        </Box>
      </PrimaryToolbar>

      <TableStateProvider
        state={mergedState}
        handlers={mergedHandlers}
        columns={columns}
        rows={data?.rows ?? []}
        tableId={tableId}
        loading={isFetching}
        context={context}
      >
        <SecondaryToolbar justifyContent="flex-end">
          <ColumnChooser />
          <CsvDownloadButton csvDownloadTitle="Reasons & Recommendations" />
          <RefreshButton onClick={onRefetch} loading={isFetching} />
        </SecondaryToolbar>

        <BaseTable>
          <RemotePagingPlugin totalCount={data?.totalCount ?? 0} />
          <ColumnResizingPlugin />
        </BaseTable>
      </TableStateProvider>
    </section>
  );
};

export const ReasonsRecommendations = compose(withStyles(styles, { withTheme: true }))(
  ({ classes, accountId }: any) => {
    const [activeTab, setActiveTab] = useState<ActiveTab>(ActiveTab.INSIGHTS);

    const handleTabChange = (
      event: React.SyntheticEvent,
      newValue: ActiveTab
    ) => {
      setActiveTab(newValue);
    };

    return (
      <section className={classes.mainContainer}>
        <div style={{ marginBottom: '0.25rem' }}>
          <h1
            className={`table-heading ${classes.headingMargins}`}
            style={{ marginBottom: '1rem' }}
          >
            Reasons & Recommendations
          </h1>
          <div className="flex--spaced" style={{ alignItems: 'center' }}>
            <div className={classes.tabsContainer}>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                TabIndicatorProps={{ sx: { display: 'none' } }}
              >
                <Tab
                  label="Insights"
                  value={ActiveTab.INSIGHTS}
                  className={classes.tabContainer}
                  disableRipple
                />
                <Tab
                  label="Informational"
                  value={ActiveTab.INFORMATION}
                  className={classes.tabContainer}
                  disableRipple
                />
              </Tabs>
            </div>
          </div>
        </div>

        <Box role="tabpanel">
          {activeTab === ActiveTab.INSIGHTS && (
            <ReasonsRecommendationsTable
              classes={classes}
              accountId={accountId}
              recommendationTab={ActiveTab.INSIGHTS}
              tableId={TableId.REASONS_RECOMMENDATIONS_INSIGHTS}
            />
          )}
          {activeTab === ActiveTab.INFORMATION && (
            <ReasonsRecommendationsTable
              classes={classes}
              accountId={accountId}
              recommendationTab={ActiveTab.INFORMATION}
              tableId={TableId.REASONS_RECOMMENDATIONS_INFORMATION}
            />
          )}
        </Box>
      </section>
    );
  }
);
